import { useEffect, useRef } from 'react'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  COMPLETE_INDEED_CONNECTION,
  JOB_SOURCE_CONNECTIONS_QUERY,
} from 'src/graphql/jobSourceConnections.graphql'
import { setSearchParam } from 'src/lib/url-search-params.utils'

const INDEED_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Indeed connection was cancelled.',
  missing_code_or_state: 'Indeed connection failed. Please try again.',
  connection_failed: 'Indeed connection failed. Please try again.',
  invalid_oauth_state: 'Indeed connection expired. Please start again.',
  cache_unavailable: 'Indeed connection failed. Please try again.',
  missing_credentials: 'Indeed integration is not configured. Contact support.',
  missing_redirect_uri: 'Indeed integration is not configured. Contact support.',
  token_exchange_failed: 'Indeed authorization failed. Please try again.',
  employer_info_failed: 'Could not read your Indeed employer account. Please try again.',
  oauth_error: 'Indeed connection failed. Please try again.',
}

function clearIndeedQueryParams() {
  setSearchParam('indeedPending', '')
  setSearchParam('indeedError', '')
  setSearchParam('indeedConnected', '')
}

const IndeedOAuthCompletion = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const pendingToken = queryParams.get('indeedPending')
  const indeedError = queryParams.get('indeedError')
  const hasCompleted = useRef(false)
  const hasShownError = useRef(false)

  const [completeIndeedConnection] = useMutation(COMPLETE_INDEED_CONNECTION, {
    onCompleted: () => {
      toast('Indeed connected successfully', 'success')
      clearIndeedQueryParams()
    },
    onError: (error) => {
      toast(error.message || 'Failed to connect Indeed', 'error')
      clearIndeedQueryParams()
    },
    refetchQueries: [JOB_SOURCE_CONNECTIONS_QUERY],
  })

  useEffect(() => {
    if (indeedError && !hasShownError.current) {
      hasShownError.current = true
      toast(
        INDEED_ERROR_MESSAGES[indeedError] || 'Failed to connect Indeed',
        'error'
      )
      clearIndeedQueryParams()
    }
  }, [indeedError])

  useEffect(() => {
    if (pendingToken && !hasCompleted.current) {
      hasCompleted.current = true
      completeIndeedConnection({ variables: { pendingToken } })
    }
  }, [pendingToken, completeIndeedConnection])

  return null
}

export default IndeedOAuthCompletion
